import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RegistrationV2State } from '../../store/registrationV2/types.ts';
import * as thunks from '../../store/registrationV2/thunks.ts';
import * as actions from '../../store/registrationV2/actions.ts';
import { setLoginAnimation, setLoginAnimationType } from '../../store/actions/authActions.js';
import { getRegistrationEmail, getFromLoginValidation, clearFromLoginValidation } from '../../utils/registration/registrationStorage.ts';
import { navigateAfterRegistration } from '../../utils/onboardingV2/onboardingNavigation.ts';

export const useRegistrationFlow = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const state = useSelector((state: any): RegistrationV2State => state.registrationV2);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const stepParam = urlParams.get('step');
    const fromLoginValidation = getFromLoginValidation();
    const emailFromLogin = getRegistrationEmail();

    if (stepParam === 'code-validation' && fromLoginValidation && emailFromLogin) {
      dispatch(actions.setEmail(emailFromLogin));
      dispatch(actions.setStep('code'));
      clearFromLoginValidation();
    }
  }, [location.search, dispatch]);

  useEffect(() => {
    if (state.codeValidation.data && state.email) {
      dispatch(actions.setValidatedEmail(state.email));
      dispatch(actions.setStep('password'));
    }
  }, [state.codeValidation.data, state.email, dispatch]);

  useEffect(() => {
    if (state.completeRegistration.data) {
      const skipOnboarding = state.completeRegistration.skipOnboarding;
      const finalizeNavigation = (skills: any[]) => {
        navigateAfterRegistration(
          navigate,
          dispatch,
          skipOnboarding,
          skills
        );
        dispatch(setLoginAnimation(false));
        dispatch(setLoginAnimationType('login'));
      };
      
      if (skipOnboarding && !state.skills.length) {
        (dispatch(thunks.loadSkillsForRegistration() as any) as Promise<any>)
          .then((skills: any) => {
            finalizeNavigation(skills || []);
          })
          .catch(() => {
            finalizeNavigation([]);
          });
      } else if (!skipOnboarding && !state.skills.length) {
        (dispatch(thunks.loadSkillsForRegistration() as any) as Promise<any>)
          .then((skills: any) => {
            finalizeNavigation(skills || []);
          })
          .catch(() => {
            finalizeNavigation([]);
          });
      } else {
        finalizeNavigation(state.skills);
      }
    }
  }, [state.completeRegistration.data, state.completeRegistration.skipOnboarding, state.skills.length, navigate, dispatch]);

  const createPreRegister = useCallback((email: string) => {
    return dispatch(thunks.createPreRegisterV2(email) as any);
  }, [dispatch]);

  const validateVerificationCode = useCallback((email: string, code: string) => {
    return dispatch(thunks.validateVerificationCodeV2(email, code) as any);
  }, [dispatch]);

  const resendVerificationCode = useCallback((email: string) => {
    return dispatch(thunks.resendVerificationCodeV2(email) as any);
  }, [dispatch]);

  const completeUserRegistration = useCallback((email: string, password: string, skipOnboarding: boolean = false) => {
    return dispatch(thunks.completeUserRegistrationV2(email, password, skipOnboarding) as any);
  }, [dispatch]);

  const loadSkillsForRegistration = useCallback(() => {
    return dispatch(thunks.loadSkillsForRegistration() as any);
  }, [dispatch]);

  const setEmail = useCallback((email: string) => {
    dispatch(actions.setEmail(email));
  }, [dispatch]);

  const setStep = useCallback((step: 'email' | 'code' | 'password') => {
    dispatch(actions.setStep(step));
  }, [dispatch]);

  const clearFlow = useCallback(() => {
    dispatch(actions.clearFlow());
  }, [dispatch]);

  return {
    ...state,
    createPreRegister,
    validateVerificationCode,
    resendVerificationCode,
    completeUserRegistration,
    loadSkillsForRegistration,
    setEmail,
    setStep,
    clearFlow,
  };
};
